import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";

import { BaseModal } from "component/Modal/BaseModal";
import LoadingButton from "component/Button/LoadingButton";
import {
  EXTERNAL_SERVICE_CODE,
  ExternalServiceCodeType,
} from "types/externalService";
import {
  CONSUMABLE_PART_TYPE,
  ConsumablePart,
  ConsumablePartType,
  getConsumablePartTypeDisplayLabel,
} from "utils/robot/consumablePart";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { handleError } from "utils/error/handleError";

type OnResetInput = {
  type: ConsumablePartType;
  usage: number;
};

const itemSchema = z.object({
  type: z.string(),
  value: z.number().min(0).max(999),
});
const formSchema = z.object({
  parts: z.array(itemSchema),
});

type FormInput = z.infer<typeof formSchema>;

type ResetModalProps = {
  open: boolean;
  onClose: () => void;
  onReset: (input: OnResetInput[]) => void;
  isResetting: boolean;
  exSrvCode: ExternalServiceCodeType;
  parts: ConsumablePart[];
  robotName: string;
};

export const ResetConsumablePartsModal = ({
  open,
  onClose,
  isResetting,
  onReset,
  exSrvCode,
  parts,
  robotName,
}: ResetModalProps) => {
  const fieldMeta = parts.map((p) => getFieldMetadata({ part: p, exSrvCode }));

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      parts: fieldMeta.map((m) => ({ type: m.type, value: m.initialValue })),
    },
  });

  const onSubmit = async (data: FormInput) => {
    try {
      const input: OnResetInput[] = data.parts.map((d) => {
        const meta = fieldMeta.find((f) => f.type === d.type);
        if (!meta) {
          console.log({ msg: "データを取得できません", data: d });
          throw new Error(`入力データを取得できません: ${d.type}`);
        }
        const maxValue = meta.maxValue;
        switch (exSrvCode) {
          case EXTERNAL_SERVICE_CODE.DREAME:
            switch (d.type) {
              case CONSUMABLE_PART_TYPE.mainBrush:
              case CONSUMABLE_PART_TYPE.sideBrush:
              case CONSUMABLE_PART_TYPE.filter:
                // 時間 => 秒に変換
                return {
                  type: d.type,
                  usage: (maxValue - d.value) * 3600,
                };
              case CONSUMABLE_PART_TYPE.dustBag:
              case CONSUMABLE_PART_TYPE.mopPad:
                return {
                  type: d.type,
                  usage: maxValue - d.value,
                };
            }
        }

        console.log({ msg: "不明なコードです", code: exSrvCode, type: d.type });
        throw new Error(`不明なコードです`);
      });

      onReset(input);
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlineIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography>パーツ使用可能時間変更</Typography>
        </Box>
      }
      children={
        <>
          <Typography>{`${robotName} の消耗品の使用可能時間を変更します`}</Typography>
          <form onSubmit={handleSubmit(onSubmit)} id="part-form">
            <Stack spacing={2}>
              {fieldMeta.map((field, index) => {
                return (
                  <Box
                    key={`field-${index}`}
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography>{field.displayLabel}</Typography>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"center"}
                      width={"128px"}
                      gap={1}
                      sx={{
                        width: { xs: "128px", md: "150px" },
                      }}
                    >
                      <Controller
                        name={`parts.${index}.value`}
                        control={control}
                        render={({ field: controllerField }) => (
                          <TextField
                            type="text"
                            inputMode="numeric"
                            {...controllerField}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const cleaned = raw.replace(/^0+(?=\d)/, "");
                              let num = Number(cleaned);
                              if (isNaN(num)) {
                                return;
                              }
                              if (num > field.maxValue) {
                                num = field.maxValue;
                              }

                              controllerField.onChange(num);
                            }}
                            sx={{
                              width: "80px",
                            }}
                            error={!!errors.parts?.[index]?.value}
                            helperText={errors.parts?.[index]?.value?.message}
                          />
                        )}
                      />
                      <Typography>{field.unitLabel}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </form>
        </>
      }
      actions={
        <Stack direction={"row"} spacing={2}>
          <Button
            disabled={isResetting}
            variant="outlined"
            onClick={onClose}
            autoFocus
          >
            キャンセル
          </Button>
          <LoadingButton
            isLoading={isResetting}
            disabled={!isDirty}
            variant="contained"
            form="part-form"
            type="submit"
            sx={{ minWidth: "120px" }}
          >
            保存
          </LoadingButton>
        </Stack>
      }
    />
  );
};

type FieldMetadata = {
  type: ConsumablePartType;
  initialValue: number;
  maxValue: number;
  displayLabel: string;
  unitLabel: string;
};

type GetFieldMetadataParams = {
  part: ConsumablePart;
  exSrvCode: ExternalServiceCodeType;
};

const getFieldMetadata = ({
  part,
  exSrvCode,
}: GetFieldMetadataParams): FieldMetadata => {
  const result: FieldMetadata = {
    type: part.type,
    initialValue: 0,
    maxValue: 0,
    displayLabel: getConsumablePartTypeDisplayLabel(part.type, exSrvCode),
    unitLabel: "",
  };

  const diff = part.max - part.usage;
  switch (exSrvCode) {
    case EXTERNAL_SERVICE_CODE.DREAME:
      switch (part.type) {
        case CONSUMABLE_PART_TYPE.mainBrush:
          result.maxValue = 300;
          result.initialValue = Math.floor(diff / 3600);
          result.unitLabel = "時間";
          break;
        case CONSUMABLE_PART_TYPE.sideBrush:
          result.maxValue = 200;
          result.initialValue = Math.floor(diff / 3600);
          result.unitLabel = "時間";
          break;
        case CONSUMABLE_PART_TYPE.filter:
          result.maxValue = 150;
          result.initialValue = Math.floor(diff / 3600);
          result.unitLabel = "時間";
          break;
        case CONSUMABLE_PART_TYPE.mopPad:
          result.maxValue = 90;
          result.initialValue = Math.floor(diff);
          result.unitLabel = "日";
          break;
        case CONSUMABLE_PART_TYPE.dustBag:
          result.maxValue = 90;
          result.initialValue = Math.floor(diff);
          result.unitLabel = "日";
          break;
      }
  }
  return result;
};
